/* MULTI-SEGMENT EFFECTS, AS ONE ROW.

   Wall of Force is not one shape: it is ten 10 ft panels, placed as ten
   separate Roll20 tokens and tracked as ten ActiveEffects named
   "Wall of Force (ten panels) 1/10" ... "10/10". That is right -- the DM drags
   each panel where they want it, and each has to be removable on its own.

   What is wrong is showing it as ten rows. The active list becomes ten near
   identical lines, and -- worse -- every panel is `armed` (needsPlacing is
   mobility !== "linked"), so the DM gets ten "is being placed" banners and has
   to click "It is placed - roll" ten times for one spell.

   So the grouping lives in the VIEW, not in the model: nothing here changes
   how a segment is placed, resolved or removed. The panels stay ten effects.
   They are drawn, confirmed and cancelled as one, and the group opens to reach
   any single panel -- so nothing that worked on a lone panel stops working.

   Grouping keys on the "i/n" marker rather than on caster+key alone, because
   two unrelated castings of the same spell must not silently merge into one
   row whose count the DM cannot explain. */

export type Segment = { base: string; i: number; n: number };

/* "Wall of Force (ten panels) 3/10" -> { base, i: 3, n: 10 }, else null.

   Written without a regular expression on purpose. This repository has been
   bitten repeatedly by backslashes being eaten between a tool and a file, and
   a silently-broken pattern here fails OPEN: every segment parses as null,
   every panel gets its own row, and the flood this module exists to stop comes
   back with no test able to tell you why. Index arithmetic cannot be mangled. */
export function segmentOf(name: string): Segment | null {
  const s = String(name || "").trim();
  const sp = s.lastIndexOf(" ");
  if (sp <= 0) return null;
  const base = s.slice(0, sp), tail = s.slice(sp + 1);
  const bar = tail.indexOf("/");
  if (bar <= 0 || bar === tail.length - 1) return null;
  const i = Number(tail.slice(0, bar)), n = Number(tail.slice(bar + 1));
  if (!Number.isInteger(i) || !Number.isInteger(n)) return null;
  /* n < 2 is not a group: "1/1" is one thing, and drawing it as a group would
     hide a lone effect behind a disclosure for no gain. */
  if (n < 2 || i < 1 || i > n) return null;
  return { base, i, n };
}

export type Segmented = { id: string; key: string; casterId: string; name: string };
export type Group<T extends Segmented> = {
  /** The lead member's id: stable across re-renders, so a group the DM has
   *  opened stays open while the move feed ticks every two seconds. */
  id: string;
  lead: T; members: T[];
  /** The name without its "i/n" marker, or the whole name when ungrouped. */
  base: string;
  /** How many are actually here, and how many the spell claimed. They differ
   *  when Roll20 refused a panel part way -- which the DM should see. */
  placed: number; of: number;
  many: boolean;
};

/** One row per effect, except that segments of one casting share a row. Input
 *  order is kept: a group sits where its FIRST member sat. */
export function groupSegments<T extends Segmented>(effects: readonly T[]): Group<T>[] {
  const rows: Group<T>[] = [];
  const at = new Map<string, Group<T>>();
  for (const e of effects || []) {
    if (!e) continue;
    const seg = segmentOf(e.name);
    if (!seg) { rows.push({ id: e.id, lead: e, members: [e], base: e.name, placed: 1, of: 1, many: false }); continue; }
    const gk = `${e.casterId}\u0000${e.key}\u0000${seg.n}`;
    const found = at.get(gk);
    if (found) { found.members.push(e); found.placed = found.members.length; found.many = true; continue; }
    const row: Group<T> = { id: e.id, lead: e, members: [e], base: seg.base, placed: 1, of: seg.n, many: false };
    at.set(gk, row); rows.push(row);
  }
  return rows;
}

/** How many DISTINCT creatures the group covers. Panels overlap at the corners,
 *  so summing each member's `inside` would count a creature once per panel and
 *  tell the DM there are more enemies in the wall than exist. */
export const insideUnion = (members: readonly { inside: string[] }[]): number =>
  new Set((members || []).flatMap((m) => m?.inside || [])).size;

/* THIS ROUND AT THE TOP, EARLIER ROUNDS BELOW AS HISTORY.

   Reported 2026-10-01: the effects window "reads backwards". The DM is looking
   at whoever is top of the initiative, so what is happening on that character's
   turn should be in the panel right above the buttons they are about to press -
   not scrolled past three rounds of spent areas.

   An effect placed before this existed carries no round. Those are shown WITH
   the current round rather than filed away: an effect still on the map is still
   the DM's problem, and hiding one in a collapsed history because the record
   predates the stamp would be the worst possible reading of "history". */
export interface Rounded { placedRound?: number }

export interface RoundSection<T> { round: number | null; groups: T[] }

/** Takes either the effects themselves or the groups they were folded into, and
 *  gives back the same kind it was handed. */
export function byRound<T extends Rounded | { lead: Rounded }>(items: readonly T[], currentRound: number): { now: T[]; history: RoundSection<T>[] } {
  const roundOf = (item: T): number | null => {
    const lead = (item as { lead?: Rounded }).lead ?? (item as Rounded);
    const r = Number(lead?.placedRound);
    return Number.isFinite(r) && r > 0 ? r : null;
  };
  const now: T[] = [];
  const earlier = new Map<number, T[]>();
  for (const g of items || []) {
    if (!g) continue;
    const r = roundOf(g);
    if (r === null || r >= currentRound) now.push(g);
    else earlier.set(r, [...(earlier.get(r) || []), g]);
  }
  // Most recent first: the round that just ended is the one still being asked about.
  const history = [...earlier.keys()].sort((a, b) => b - a).map((round) => ({ round, groups: earlier.get(round)! }));
  return { now, history };
}
